import { datadogRum } from '@datadog/browser-rum';
import { Download } from '@carbon/react/icons';
import {
  Button,
  InlineLoading,
  OverflowMenu,
  OverflowMenuItem,
} from '@carbon/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Column } from '@tanstack/react-table';
import ErrorToast from 'components/error-toast';
import { exportTableToCsv, exportTableToXlsx } from 'utils/tables';
import { useDataTableContext } from './context';
import { ExportConfig, ExportFormat, ExportPreset } from './types';
import { formatCellValue } from './utils';
import styles from './export-button.module.scss';

interface ExportButtonProps {
  config?: ExportConfig;
}

const DEFAULT_CLIENT_PRESET: ExportPreset = {
  name: 'Export',
  format: 'csv',
  filename: 'export',
  exportAll: false,
  useRawValues: false,
  columns: 'visible',
};

const FORMAT_LABELS: Record<ExportFormat, string> = {
  csv: 'CSV (.csv)',
  xlsx: 'Excel (.xlsx)',
};

const NO_COLUMNS_REASON = 'No columns are visible to export';

/**
 * The key of a linked column's URL column.
 * @param {string} id - The linked column's id
 * @return {string} The URL column's key
 */
const urlKey = (id: string) => `${id} url`;

/**
 * The export's headers: each column, then a "<header> URL" column when it links.
 * @param {Column<unknown, unknown>[]} cols - The exported columns
 * @param {ExportPreset} preset - The export recipe
 * @return {Array<{key: string, header: string}>} The headers
 */
const exportHeaders = (
  cols: Column<unknown, unknown>[],
  preset: ExportPreset
) =>
  cols.flatMap((col) => {
    const metaLabel = col.columnDef.meta?.label;
    const display =
      metaLabel ??
      (typeof col.columnDef.header === 'string'
        ? col.columnDef.header
        : col.id);
    const header = preset.transformHeader
      ? preset.transformHeader(col.id, display)
      : display;
    return col.columnDef.meta?.getExportHref
      ? [
          { key: col.id, header },
          { key: urlKey(col.id), header: `${header} URL` },
        ]
      : [{ key: col.id, header }];
  });

/**
 * Export button for a DataTable. Each `ExportPreset` describes one export
 * recipe; the button renders a single icon for one preset or an overflow
 * menu for two or more. See `ExportPreset` for field semantics.
 * @param {ExportButtonProps} props - Component props
 * @return {JSX.Element} The export button or menu element
 */
export function ExportButton({ config = {} }: ExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [failed, setFailed] = useState(false);
  const { table, globalFilter, columnFilters, sorting } = useDataTableContext();
  const abortRef = useRef<AbortController>();
  useEffect(() => () => abortRef.current?.abort(), []);

  const effectivePresets = useMemo<ExportPreset[]>(() => {
    if (config.exportPresets && config.exportPresets.length > 0) {
      return config.exportPresets;
    }
    return [DEFAULT_CLIENT_PRESET];
  }, [config.exportPresets]);

  const canExport = (preset: ExportPreset) => {
    if (preset.exportAll) {
      return table.getCoreRowModel().rows.length > 0;
    }
    return table.getFilteredRowModel().rows.length > 0;
  };

  const resolvePresetColumns = useCallback(
    (preset: ExportPreset) => {
      // Pinned columns render first/last, so read them in on-screen order.
      const pool =
        (preset.columns ?? 'visible') === 'all'
          ? [
              ...table.getLeftLeafColumns(),
              ...table.getCenterLeafColumns(),
              ...table.getRightLeafColumns(),
            ]
          : [
              ...table.getLeftVisibleLeafColumns(),
              ...table.getCenterVisibleLeafColumns(),
              ...table.getRightVisibleLeafColumns(),
            ];
      return pool.filter((col) => {
        if (col.id === 'expander') return false;
        return col.columnDef.meta?.isExportable !== false;
      });
    },
    [table]
  );

  // Blocked by its own reason or by resolving zero columns (client exports
  // always use them; server ones only when `columns` is set).
  const getStatus = (preset: ExportPreset) => {
    const usesColumns = !preset.onExport || preset.columns !== undefined;
    const reason =
      preset.disabledReason ||
      (usesColumns && resolvePresetColumns(preset).length === 0
        ? NO_COLUMNS_REASON
        : undefined);
    return { reason, available: !reason && canExport(preset) };
  };
  const statuses = effectivePresets.map(getStatus);
  const isDisabled = isExporting || !statuses.some((s) => s.available);
  // A disabled menu won't open, so its trigger carries the items' reasons.
  const menuReason = statuses.some((s) => s.available)
    ? undefined
    : [...new Set(statuses.map((s) => s.reason).filter(Boolean))].join('; ');

  const handleExport = useCallback(
    async (preset: ExportPreset) => {
      const controller = new AbortController();
      abortRef.current = controller;
      setIsExporting(true);
      try {
        if (preset.onExport) {
          const columnsArg =
            preset.columns === undefined
              ? undefined
              : resolvePresetColumns(preset).map((c) => c.id);
          await preset.onExport(
            preset.format,
            { globalFilter, columnFilters, sorting },
            preset.name,
            columnsArg
          );
          return;
        }

        const cols = resolvePresetColumns(preset);
        const headers = exportHeaders(cols, preset);

        const sourceRows: { original: unknown; index: number }[] =
          preset.fetchRows
            ? (await preset.fetchRows(controller.signal)).map(
                (original, index) => ({
                  original,
                  index,
                })
              )
            : (preset.exportAll
                ? table.getCoreRowModel()
                : table.getFilteredRowModel()
              ).rows;

        const data = sourceRows.map((row) => {
          const out: Record<string, unknown> = {};
          cols.forEach((col) => {
            const meta = col.columnDef.meta;
            if (meta?.getExportValue) {
              out[col.id] = meta.getExportValue(row.original);
            } else {
              // As row.getValue reads it, for a fetched row too.
              const value = col.accessorFn?.(row.original, row.index);
              out[col.id] = preset.useRawValues
                ? value
                : formatCellValue(value, meta);
            }
            if (meta?.getExportHref) {
              const href = meta.getExportHref(row.original);
              const url = href
                ? new URL(href, window.location.origin).href
                : null;
              out[urlKey(col.id)] = preset.useRawValues
                ? url
                : formatCellValue(url);
            }
          });
          return out;
        });

        const exportOptions = {
          filename: preset.filename ?? 'export',
          columns: headers,
          data,
        };
        if (preset.format === 'csv') {
          await exportTableToCsv(exportOptions);
        } else {
          exportTableToXlsx(exportOptions);
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        datadogRum.addError(error);
        if (window.Rollbar) window.Rollbar.error(error);
        setFailed(true);
      } finally {
        setIsExporting(false);
      }
    },
    [table, resolvePresetColumns, globalFilter, columnFilters, sorting]
  );

  return (
    <>
      {failed && (
        <ErrorToast
          message="Export failed"
          onClose={() => {
            setFailed(false);
            return true;
          }}
        />
      )}
      {effectivePresets.length >= 2 ? (
        <OverflowMenu
          renderIcon={isExporting ? InlineLoading : Download}
          flipped
          size="lg"
          iconDescription={menuReason || 'Export'}
          className={styles.exportButton}
          menuOptionsClass="exportMenuOptions"
          disabled={isDisabled}
        >
          {effectivePresets.map((preset, i) => (
            <OverflowMenuItem
              key={preset.name}
              itemText={`${preset.name} (.${preset.format})`}
              disabled={!statuses[i].available}
              requireTitle={Boolean(statuses[i].reason)}
              title={statuses[i].reason}
              onClick={() => handleExport(preset)}
            />
          ))}
        </OverflowMenu>
      ) : (
        <Button
          hasIconOnly
          renderIcon={isExporting ? InlineLoading : Download}
          kind="ghost"
          size="lg"
          iconDescription={
            statuses[0].reason ??
            `Export as ${FORMAT_LABELS[effectivePresets[0].format]}`
          }
          tooltipPosition="bottom"
          onClick={() => handleExport(effectivePresets[0])}
          className={styles.exportButton}
          disabled={isDisabled}
        />
      )}
    </>
  );
}
